import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  ConnectionMode,
  Handle,
  Position,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  useNodesState,
  useEdgesState,
  useReactFlow,
  type Connection,
  type Node,
  type Edge,
  type NodeChange,
  type EdgeChange,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import type { TreeView, GraphView } from '@/features/Visualizer/types';

interface Props {
  kind: 'tree' | 'graph';
  value: TreeView | GraphView;
  onChange: (next: TreeView | GraphView) => void;
}

interface LabelNodeData {
  label: string;
  onLabelChange: (nodeId: string, next: string) => void;
}

/**
 * The one custom node type this canvas registers. Renders the node's
 * editable value/label as a plain text input rather than a static span, so
 * authoring a node's displayed value doesn't require dropping to raw JSON —
 * only its `id` (used for edge/child wiring) is immutable once created.
 */
function LabelNode({ id, data }: NodeProps) {
  const { label, onLabelChange } = data as unknown as LabelNodeData;
  return (
    <div className="rounded-md border border-[#d1d5db] bg-white px-2 py-1 text-xs shadow-sm">
      {/* A single handle, not one per role — two dots (a "target" up top, a
          "source" down below) read as two different, fixed-direction
          connection points, which is exactly what a trainer found confusing.
          `connectionMode="loose"` (set on the <ReactFlow> below) means any
          handle can both start AND complete a connection regardless of its
          declared `type`, so one handle is enough; combined with the
          enlarged size here and the bigger `connectionRadius` below, a drag
          no longer needs to land pixel-precisely on it either. */}
      <Handle type="source" position={Position.Top} className="!h-3 !w-3" />
      <input
        className="nodrag w-20 border-none bg-transparent text-center text-xs outline-none"
        value={label}
        onChange={(e) => onLabelChange(id, e.target.value)}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

const nodeTypes = { labelNode: LabelNode };

function labelOf(kind: 'tree' | 'graph', node: { value?: number | string; label?: string; id: string }): string {
  const raw = kind === 'tree' ? node.value : node.label;
  return raw === undefined || raw === null ? node.id : String(raw);
}

/**
 * Simple deterministic layout (no persisted geometry) — a grid for both tree
 * and graph. Good enough for authoring; the real renderer (Visualizer/Player)
 * computes its own layout independently at playback time. React Flow's
 * `position` on each node exists purely so the canvas has something to draw
 * and drag — it is never read back out into the serialized `View`.
 */
function layoutNodes(
  kind: 'tree' | 'graph',
  value: TreeView | GraphView,
  onLabelChange: (nodeId: string, next: string) => void
): Node[] {
  return value.nodes.map((n, i) => ({
    id: n.id,
    type: 'labelNode',
    position: { x: 120 * (i % 5), y: 120 * Math.floor(i / 5) },
    data: { label: labelOf(kind, n), onLabelChange } satisfies LabelNodeData,
  }));
}

function edgesFromValue(kind: 'tree' | 'graph', value: TreeView | GraphView): Edge[] {
  if (kind === 'graph') {
    return (value as GraphView).edges.map((e, i) => ({
      id: `e${i}-${e.from}-${e.to}`,
      source: e.from,
      target: e.to,
    }));
  }
  const edges: Edge[] = [];
  (value as TreeView).nodes.forEach((n) => {
    (n.children ?? []).forEach((c, idx) => {
      if (c) edges.push({ id: `${n.id}-${idx}-${c}`, source: n.id, target: c });
    });
  });
  return edges;
}

/** Generates a node id that doesn't collide with any existing one, e.g. `node-1`, `node-2`. */
function nextNodeId(existingIds: string[]): string {
  const used = new Set(existingIds);
  let i = existingIds.length + 1;
  while (used.has(`node-${i}`)) i += 1;
  return `node-${i}`;
}

/**
 * Pure serializer: turns the current React Flow node/edge state into the
 * plain `TreeView | GraphView` shape that `onChange` is called with.
 *
 * This is intentionally the ONLY place that builds the outgoing payload, and
 * it is exported so tests can invoke the exact function the component's
 * `onConnect`/`onEdgesChange`/`onNodesChange`/`onDrop` handlers call — rather
 * than relying on jsdom firing simulated React Flow pointer/drag events
 * (which it does not do reliably). It reads only `id` and `data.label` off
 * `nodes`/`edges` and never touches `position`, so the result can never carry
 * `x`/`y`.
 *
 * Every field this authoring canvas has no editing affordance for — graph
 * `caption`/`layout`/per-edge `weight`/`directed`/`tone`, tree `caption`/
 * `cursor`/per-node's own `marks` — is preserved by spreading `previous` and
 * matching on node/edge id, rather than rebuilt from scratch on every
 * connect/delete/add (which would silently drop anything authored outside
 * this canvas, e.g. via the raw-JSON fallback or a future dedicated editor
 * for those fields).
 *
 * Tree `children` is POSITIONAL (index 0 = left, 1 = right, `null` keeps the
 * slot — see `Visualizer/types.ts`), so rebuilding it from edge-insertion
 * order would silently reassign a right-only child to the left slot. Instead,
 * each node's previous positional `children` array is the base: slots whose
 * edge no longer exists are cleared to `null`, slots whose edge still exists
 * keep their position, and any genuinely NEW child (an edge that didn't exist
 * in `previous`) fills the first empty (`null`) slot, or is appended only if
 * no slot is free.
 */
export function serializeCanvas(
  kind: 'tree' | 'graph',
  nodes: Node[],
  edges: Edge[],
  previous: TreeView | GraphView
): TreeView | GraphView {
  const ids = nodes.map((n) => n.id);
  const idSet = new Set(ids);
  const labelById = new Map(
    nodes.map((n) => [n.id, (n.data as unknown as LabelNodeData | undefined)?.label])
  );
  // What `LabelNode`'s input would show for each PREVIOUSLY-persisted node if
  // the user had touched nothing — i.e. `labelOf(prevNode)`. A brand new node
  // (not in `previous`) has no entry here. Comparing the live input value
  // against this, rather than trusting the input value on its own, is what
  // lets an untouched tree node keep its numeric `value` (e.g. `1`) instead
  // of being coerced to the string `"1"` on every unrelated connect/delete —
  // the input never distinguishes "user typed the digit 1" from "never
  // touched, still showing the stringified default".
  const origLabelById = new Map(previous.nodes.map((n) => [n.id, labelOf(kind, n)]));

  const isEdited = (id: string) => {
    const live = labelById.get(id);
    return live !== undefined && live !== origLabelById.get(id);
  };

  if (kind === 'graph') {
    const prevGraph = previous as GraphView;
    const prevNodeById = new Map(prevGraph.nodes.map((n) => [n.id, n]));
    const prevEdgeByPair = new Map(prevGraph.edges.map((e) => [`${e.from}->${e.to}`, e]));

    const graph: GraphView = {
      ...prevGraph,
      kind: 'graph',
      nodes: ids.map((id) => ({
        ...prevNodeById.get(id),
        id,
        label: isEdited(id) ? labelById.get(id) : prevNodeById.get(id)?.label,
      })),
      edges: edges.map((e) => {
        const prevEdge = prevEdgeByPair.get(`${e.source}->${e.target}`);
        return { ...prevEdge, from: e.source, to: e.target };
      }),
    };
    if (graph.marks) {
      // Drop marks referencing a node that no longer exists after a delete —
      // a dangling `nodeId` reference would be worse than losing the mark.
      graph.marks = graph.marks.filter((m) => idSet.has(m.nodeId));
    }
    return graph;
  }

  const prevTree = previous as TreeView;
  const prevNodeById = new Map(prevTree.nodes.map((n) => [n.id, n]));

  const childrenOf: Record<string, string[]> = {};
  edges.forEach((e) => {
    childrenOf[e.source] = [...(childrenOf[e.source] ?? []), e.target];
  });

  const nextNodes = ids.map((id) => {
    const prevNode = prevNodeById.get(id);
    const prevChildren = prevNode?.children ?? [];
    const currentTargets = new Set(childrenOf[id] ?? []);

    // Start from the EXISTING positional array: keep a slot's occupant if its
    // edge still exists, otherwise clear it to `null`.
    const merged: Array<string | null> = prevChildren.map((c) =>
      c && currentTargets.has(c) ? c : null
    );
    merged.forEach((c) => {
      if (c) currentTargets.delete(c);
    });
    // Whatever's left in `currentTargets` is a genuinely NEW child relation —
    // place it in the first empty slot rather than reordering existing ones.
    for (const child of currentTargets) {
      const emptySlot = merged.findIndex((c) => c === null);
      if (emptySlot === -1) merged.push(child);
      else merged[emptySlot] = child;
    }

    return {
      ...prevNode,
      id,
      value: isEdited(id) ? labelById.get(id)! : prevNode?.value ?? id,
      children: merged,
    };
  });

  const tree: TreeView = {
    ...prevTree,
    kind: 'tree',
    rootId: prevTree.rootId,
    nodes: nextNodes,
  };
  if (tree.marks) {
    tree.marks = tree.marks.filter((m) => idSet.has(m.nodeId));
  }
  return tree;
}

function Canvas({ kind, value, onChange }: Props) {
  const { screenToFlowPosition } = useReactFlow();

  // Stable across re-layouts — `onLabelChange` closes over this and is only
  // ever reassigned by the effect below when `value`/`kind` themselves change
  // (never on every keystroke), so `LabelNode`'s input never loses focus.
  const onLabelChange = useCallback(
    (nodeId: string, next: string) => {
      // `emit` (which calls the parent's `onChange`, updating a JsonForms-
      // managed ancestor's state) must run OUTSIDE the `setNodes` updater —
      // React invokes updater functions during its render/commit work, and
      // triggering a different component's setState from inside one is
      // exactly the "Cannot update a component while rendering a different
      // component" violation. Computing `nextNodes` as a plain value first,
      // then calling `setNodes`/`emit` as two separate statements (matching
      // every other handler in this file), keeps the updater pure.
      const nextNodes = nodesRef.current.map((n) =>
        n.id === nodeId ? { ...n, data: { ...n.data, label: next } } : n
      );
      nodesRef.current = nextNodes;
      setNodes(nextNodes);
      emit(nextNodes, edgesRef.current);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const initialEdges = useMemo(() => edgesFromValue(kind, value), [kind, value]);

  const [nodes, setNodes] = useNodesState(layoutNodes(kind, value, onLabelChange));
  const [edges, setEdges] = useEdgesState(initialEdges);

  // Authoritative, synchronously-updated mirrors of `nodes`/`edges`. React's
  // `nodes`/`edges` state variables only reflect the *last committed render*
  // — within a single user interaction (e.g. deleting a selected node),
  // React Flow can invoke `onNodesChange` and `onEdgesChange` back-to-back in
  // the same tick, before React re-renders. Reading the closed-over `nodes`/
  // `edges` (or a ref synced only on render) in the second handler would see
  // stale data. These refs are instead written to imperatively, immediately,
  // inside each handler below, so whichever handler fires second in the same
  // batch always sees the other's just-applied change.
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const edgesRef = useRef(edges);
  edgesRef.current = edges;

  // Tracks the serialized CONTENT `nodes`/`edges` currently reflect — whether
  // that content arrived via our own `emit` (below) or via the initial mount
  // value. Seeded synchronously from the mount value so the resync effect
  // below does not immediately fire on first render (it would otherwise
  // resync to identical content on every mount, which is harmless but
  // wasteful — and, worse, would fire on every re-render where the PARENT
  // simply passed a new object reference with unchanged content, which
  // happens on every keystroke elsewhere in the same form and would reset
  // any in-progress node drag positions back to the grid layout).
  const lastSyncedSerialized = useRef<string>(JSON.stringify(value));

  const emit = useCallback(
    (nextNodes: Node[], nextEdges: Edge[]) => {
      const next = serializeCanvas(kind, nextNodes, nextEdges, value);
      lastSyncedSerialized.current = JSON.stringify(next);
      onChange(next);
    },
    [kind, onChange, value]
  );

  // `useNodesState(layoutNodes(...))` / `useEdgesState(initialEdges)` above
  // only seed React Flow's internal state ONCE, on mount — same index-
  // reconciliation hazard as the pre-fix `RawJsonItemEditor` (see that
  // component's comment): `ObjectArrayControl` keys `Step.views` items by
  // array index, so when an earlier view is removed, this component instance
  // is reused for whatever view now occupies that index. Without this effect,
  // the canvas keeps rendering the REMOVED view's graph while `onChange`
  // calls would target the new item's path — silent data mismatch. Comparing
  // serialized CONTENT (not object identity) is required because `value` is a
  // plain object recreated by the parent on every render even when nothing
  // about it actually changed.
  useEffect(() => {
    const incomingSerialized = JSON.stringify(value);
    if (incomingSerialized === lastSyncedSerialized.current) return;
    lastSyncedSerialized.current = incomingSerialized;
    const nextNodes = layoutNodes(kind, value, onLabelChange);
    const nextEdges = edgesFromValue(kind, value);
    nodesRef.current = nextNodes;
    edgesRef.current = nextEdges;
    setNodes(nextNodes);
    setEdges(nextEdges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, kind]);

  const onConnect = useCallback(
    (connection: Connection) => {
      const nextEdges = addEdge(connection, edgesRef.current);
      edgesRef.current = nextEdges;
      setEdges(nextEdges);
      emit(nodesRef.current, nextEdges);
    },
    [setEdges, emit]
  );

  /**
   * Node deletion is the case the contract ("on any drag/connect/delete/add
   * calls `onChange`") previously missed:
   *  - An isolated node with no edges was removed only from React Flow's
   *    internal state, never serialized out via `onChange` — the deleted
   *    node would silently linger in whatever payload was last emitted.
   *  - A connected node's delete removes both the node (here) and its
   *    edges (via a follow-up `onEdgesChange` call from React Flow's
   *    default delete behavior). Both handlers prune edges touching the
   *    removed node id themselves and emit synchronously, so the result is
   *    correct regardless of which handler fires first or whether the
   *    other fires at all.
   */
  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      const nextNodes = applyNodeChanges(changes, nodesRef.current);
      nodesRef.current = nextNodes;
      setNodes(nextNodes);

      const removedIds = changes.filter((c) => c.type === 'remove').map((c) => c.id);
      if (removedIds.length > 0) {
        const removedSet = new Set(removedIds);
        const nextEdges = edgesRef.current.filter(
          (e) => !removedSet.has(e.source) && !removedSet.has(e.target)
        );
        edgesRef.current = nextEdges;
        setEdges(nextEdges);
        emit(nextNodes, nextEdges);
      }
    },
    [setNodes, setEdges, emit]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      const nextEdges = applyEdgeChanges(changes, edgesRef.current);
      edgesRef.current = nextEdges;
      setEdges(nextEdges);

      const removal = changes.some((c) => c.type === 'remove');
      if (removal) {
        emit(nodesRef.current, nextEdges);
      }
    },
    [setEdges, emit]
  );

  /**
   * Drag-and-drop node creation (https://reactflow.dev/examples/interaction/drag-and-drop
   * is the pattern; the mechanics are NOT that example's — see below), the
   * capability this canvas was missing entirely: there was previously no way
   * to add a node at all, only to connect/delete ones already present in the
   * persisted `View`.
   *
   * This uses Pointer Events (`pointerdown`/`pointermove`/`pointerup` on
   * `window`), not the native HTML5 Drag and Drop API (`draggable`/
   * `dataTransfer`/`onDragOver`/`onDrop`) that the linked example uses — that
   * API is driven by OS-level drag gestures that trackpads frequently never
   * generate, so a trackpad user pressing the palette chip would see nothing
   * happen at all. Pointer Events fire identically for mouse, trackpad, and
   * touch.
   */
  const canvasRef = useRef<HTMLDivElement>(null);
  const [isAddingNode, setIsAddingNode] = useState(false);
  const [addNodeGhost, setAddNodeGhost] = useState<{ x: number; y: number; overCanvas: boolean } | null>(null);

  /**
   * Plain click alternative to dragging the palette chip onto the canvas —
   * many trackpads (especially with "tap to click" enabled, the macOS
   * default) cannot perform a press-and-hold-while-moving gesture at all, so
   * a tap releases instantly with no drag ever happening. This is the
   * primary, always-works path; the drag mechanics below remain as an
   * optional shortcut. Position is a fresh grid slot — geometry is never
   * persisted (see `layoutNodes`), so where it lands cosmetically doesn't
   * matter.
   */
  const addNode = useCallback(() => {
    const id = nextNodeId(nodesRef.current.map((n) => n.id));
    const i = nodesRef.current.length;
    const newNode: Node = {
      id,
      type: 'labelNode',
      position: { x: 120 * (i % 5), y: 120 * Math.floor(i / 5) },
      data: { label: id, onLabelChange } satisfies LabelNodeData,
    };
    const nextNodes = [...nodesRef.current, newNode];
    nodesRef.current = nextNodes;
    setNodes(nextNodes);
    emit(nextNodes, edgesRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setNodes, emit, onLabelChange]);

  /**
   * Plain click alternative to dragging between two nodes' handles to
   * connect them (same trackpad rationale as `addNode`). While active,
   * clicking one node arms it as the pending source; clicking a second node
   * creates the edge and clears the pending source.
   */
  const [connectMode, setConnectMode] = useState(false);
  const [pendingSourceId, setPendingSourceId] = useState<string | null>(null);

  const onNodeClick = useCallback(
    (_event: MouseEvent, node: Node) => {
      if (!connectMode) return;
      if (!pendingSourceId) {
        setPendingSourceId(node.id);
        return;
      }
      if (pendingSourceId === node.id) {
        setPendingSourceId(null);
        return;
      }
      const nextEdges = addEdge(
        { source: pendingSourceId, target: node.id, sourceHandle: null, targetHandle: null },
        edgesRef.current
      );
      edgesRef.current = nextEdges;
      setEdges(nextEdges);
      emit(nodesRef.current, nextEdges);
      setPendingSourceId(null);
    },
    [connectMode, pendingSourceId, setEdges, emit]
  );

  const isOverCanvas = useCallback((x: number, y: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    return !!rect && x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  }, []);

  const startAddNodeDrag = useCallback(
    (event: React.PointerEvent) => {
      event.preventDefault();
      setIsAddingNode(true);
      setAddNodeGhost({ x: event.clientX, y: event.clientY, overCanvas: isOverCanvas(event.clientX, event.clientY) });
    },
    [isOverCanvas]
  );

  useEffect(() => {
    if (!isAddingNode) return;
    const handleMove = (e: PointerEvent) => {
      setAddNodeGhost({ x: e.clientX, y: e.clientY, overCanvas: isOverCanvas(e.clientX, e.clientY) });
    };
    const handleUp = (e: PointerEvent) => {
      if (isOverCanvas(e.clientX, e.clientY)) {
        const id = nextNodeId(nodesRef.current.map((n) => n.id));
        const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
        const newNode: Node = {
          id,
          type: 'labelNode',
          position,
          data: { label: id, onLabelChange } satisfies LabelNodeData,
        };
        const nextNodes = [...nodesRef.current, newNode];
        nodesRef.current = nextNodes;
        setNodes(nextNodes);
        emit(nextNodes, edgesRef.current);
      }
      setIsAddingNode(false);
      setAddNodeGhost(null);
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
    // `screenToFlowPosition`/`emit`/`onLabelChange` close over state as of
    // drag-start — fine since nothing else changes this control's data
    // mid-drag. eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddingNode, isOverCanvas, screenToFlowPosition, setNodes, emit, onLabelChange]);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={addNode}
          onPointerDown={startAddNodeDrag}
          style={{ touchAction: 'none' }}
          className="cursor-grab select-none rounded-md border border-dashed border-primary/50 bg-primary/5 px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10 active:cursor-grabbing"
        >
          + Add node
        </button>
        <button
          type="button"
          onClick={() => {
            setConnectMode((on) => !on);
            setPendingSourceId(null);
          }}
          className={`rounded-md border px-2 py-1 text-xs font-medium ${
            connectMode
              ? 'border-primary bg-primary text-white'
              : 'border-dashed border-primary/50 bg-primary/5 text-primary hover:bg-primary/10'
          }`}
        >
          {connectMode ? (pendingSourceId ? 'Click a node to connect to…' : 'Click two nodes to connect') : 'Connect nodes'}
        </button>
        <span className="text-[11px] text-[#9ca3af]">
          Type a node&apos;s value directly on it · drag its handle onto another node to connect (or use "Connect nodes")
        </span>
      </div>
      <div
        ref={canvasRef}
        data-connect-mode={connectMode}
        style={{ height: 320 }}
        className="rounded-md border border-[#ececec]"
        // Stops a click that lands on this nested canvas's own nodes/edges (e.g. selecting an
        // edge) from continuing to bubble up into the OUTER stage's ReactFlow, which would
        // otherwise also select the enclosing block card. Both this inner flow and the outer
        // stage register their own Backspace/Delete listener on `document`; if the outer block
        // ends up selected too, that keypress deletes the whole block instead of just the edge —
        // this is the fix for exactly that bug. Runs in the normal bubble phase, after this
        // inner flow's own click-to-select handlers have already fired on the actual target, so
        // inner selection still works — only the further bubble to the outer block is stopped.
        onClick={(e) => e.stopPropagation()}
      >
        {/*
          `LabelNode`'s input calls `stopPropagation` on click (so clicking
          into it to type doesn't also trigger React Flow's node
          select/drag) — which means a click landing on the input NEVER
          reaches `onNodeClick` below, and "Connect nodes" mode would look
          broken for the (very common) case of clicking near the middle of a
          node, which is mostly input. Rather than thread a "connect mode
          active" flag through every node-data construction site, disabling
          the input's pointer-events for the container's duration lets the
          click fall through to the node wrapper React Flow actually
          attaches `onNodeClick` to.
        */}
        {connectMode && (
          <style>{'[data-connect-mode="true"] .react-flow__node input { pointer-events: none; }'}</style>
        )}
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          // `loose` treats a node's top and bottom handles as interchangeable —
          // either one can start OR complete a connection, so a trainer doesn't
          // have to remember "top receives, bottom sends" when nodes are laid
          // out side-by-side rather than stacked. `connectionRadius` (default
          // 20px) is raised so releasing reasonably near a handle still snaps
          // to it, rather than requiring pixel-precise aim.
          connectionMode={ConnectionMode.Loose}
          connectionRadius={40}
          fitView
        >
          <Background />
          {/* Nodes/edges can end up anywhere near the pane's default
              bottom-left corner (especially with few nodes, laid out in
              `layoutNodes`'s top-left-origin grid) — top-right stays clear. */}
          <Controls position="top-right" />
        </ReactFlow>
      </div>
      {addNodeGhost && (
        <div
          style={{ position: 'fixed', left: addNodeGhost.x + 8, top: addNodeGhost.y + 8, pointerEvents: 'none', zIndex: 9999 }}
          className={`rounded-md px-2 py-1 text-[10px] font-semibold text-white shadow-lg ${
            addNodeGhost.overCanvas ? 'bg-primary' : 'bg-[#78716c]'
          }`}
        >
          + node
        </div>
      )}
    </div>
  );
}

export function VisualizationTreeGraphCanvas(props: Props) {
  return (
    <ReactFlowProvider>
      <Canvas {...props} />
    </ReactFlowProvider>
  );
}
